// ═══════════════════════════════════════════════════════
// QuickPanel — Panneau rapide de préparation
// Colis individuels (chaque + crée une ligne en base)
// ═══════════════════════════════════════════════════════

import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase.js';
import { compressImage } from '../lib/photos.js';
import { mergeSession } from '../lib/mergeSession.js';
import { syncSessionToAsana } from '../lib/asana.js';
import CameraCapture from './CameraCapture.jsx';

const TYPES = ['amb', 'froid', 'cartons'];
const TYPE_LABELS = { amb: 'AMBIANT', froid: 'FROID', cartons: 'CARTONS' };
const TYPE_COLORS = { amb: '#D4680A', froid: '#1A5276', cartons: '#444' };
const TYPE_BG = { amb: '#FFF8F0', froid: '#F0F6FF', cartons: '#F8F8FA' };

export default function QuickPanel({ session, user, onClose, onSessionUpdate, onOpenDetail }) {
  const [photoCount, setPhotoCount] = useState(0);
  const [productCount, setProductCount] = useState(0);
  const [totalQty, setTotalQty] = useState(0);
  const [anomalyCount, setAnomalyCount] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [confirmPrete, setConfirmPrete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [scanCode, setScanCode] = useState('');
  const [scanMsg, setScanMsg] = useState(null);
  const [scannedCaisses, setScannedCaisses] = useState([]);
  const scanInputRef = useRef(null);

  useEffect(() => {
    if (!session?.id || String(session.id).startsWith('demo-')) return;
    loadAll();
  }, [session?.id]);

  async function loadAll() {
    try {
      const [linesRes, photosRes, anomRes] = await Promise.all([
        supabase.from('elixir_prep_order_lines').select('qty').eq('session_id', session.id),
        supabase.from('elixir_prep_photos').select('id', { count: 'exact', head: true }).eq('session_id', session.id),
        supabase.from('elixir_prep_anomalies').select('id', { count: 'exact', head: true }).eq('session_id', session.id),
      ]);
      if (linesRes.data) {
        setProductCount(linesRes.data.length);
        setTotalQty(linesRes.data.reduce((sum, l) => sum + (Number(l.qty) || 0), 0));
      }
      setPhotoCount(photosRes.count || 0);
      setAnomalyCount(anomRes.count || 0);
    } catch (e) { console.warn('QuickPanel loadAll error:', e); }
  }

  // Compteurs depuis la session (source de vérité)
  const counts = {
    amb: session.nb_amb || 0,
    froid: session.nb_froid || 0,
    cartons: (session.nb_cartons || 0) + (session.nb_cartons_vrac || 0),
  };

  // ═══ MODIFIER UN COMPTEUR COLIS — simple +/- ═══
  async function addColis(type) {
    if (busy) return;
    setBusy(true);
    try {
      await syncCounters({ ...counts, [type]: counts[type] + 1 });
      // Mettre à jour le state local aussi
      counts[type] = counts[type] + 1;
    } catch (e) { console.error('addColis error:', e); }
    finally { setBusy(false); }
  }

  async function removeColis(type) {
    if (busy || counts[type] <= 0) return;
    setBusy(true);
    try {
      await syncCounters({ ...counts, [type]: counts[type] - 1 });
      counts[type] = counts[type] - 1;
    } catch (e) { console.error('removeColis error:', e); }
    finally { setBusy(false); }
  }

  async function syncCounters(newCounts) {
    const update = {
      nb_amb: newCounts.amb || 0, nb_froid: newCounts.froid || 0,
      nb_cartons: newCounts.cartons || 0, nb_cartons_vrac: 0,
      updated_at: new Date().toISOString(),
    };
    await supabase.from('elixir_prep_sessions').update(update).eq('id', session.id);
    onSessionUpdate({ ...session, ...update });
  }

  // ═══ SCAN CAISSE ═══
  async function scanCaisse(type) {
    let code = scanCode.trim().toUpperCase().replace(/[\s\-_.]/g, '');
    if (!code) return;
    const match = code.match(/^C0*(\d+)$/);
    if (!match) { setScanMsg({ ok: false, txt: 'Format invalide (C1, C42…)' }); setScanCode(''); return; }
    code = 'C' + match[1];
    setBusy(true);
    try {
      // Vérifier doublon même session
      const { data: dup } = await supabase.from('elixir_prep_caisses').select('id').eq('session_id', session.id).eq('code', code).limit(1);
      if (dup && dup.length > 0) { setScanMsg({ ok: false, txt: code + ' déjà scannée !' }); setScanCode(''); setBusy(false); return; }
      // Vérifier doublon autre pharmacie
      const sessionDate = session.date || new Date().toISOString().split('T')[0];
      const { data: otherUse } = await supabase.from('elixir_prep_caisses').select('session_id, elixir_prep_sessions!inner(partner_name, date)').eq('code', code).eq('elixir_prep_sessions.date', sessionDate).neq('session_id', session.id).limit(1);
      if (otherUse && otherUse.length > 0) { setScanMsg({ ok: false, txt: '⛔ ' + code + ' chez ' + (otherUse[0].elixir_prep_sessions?.partner_name || 'autre') }); setScanCode(''); setBusy(false); return; }

      // Bug 2: vérifier le stock — est-ce que la caisse est déjà en livraison ?
      const { data: stock } = await supabase.from('elixir_prep_caisse_stock').select('status, current_partner_name, date_sortie').eq('code', code).maybeSingle();
      if (stock && stock.status === 'en_livraison') {
        const chez = stock.current_partner_name || 'une pharmacie';
        const jours = stock.date_sortie ? Math.floor((new Date() - new Date(stock.date_sortie)) / 86400000) : '?';
        if (!confirm('⚠️ ' + code + ' est chez ' + chez + ' (depuis ' + jours + 'j).\n\nFermer l\'ancien mouvement et réutiliser ?')) {
          setScanCode(''); setBusy(false); return;
        }
        // Fermer l'ancien mouvement (retour implicite)
        await supabase.from('elixir_prep_caisse_mouvements').update({
          date_retour: new Date().toISOString(),
        }).eq('caisse_code', code).is('date_retour', null);
      }

      // Insert dans elixir_prep_caisses
      const caisseType = type === 'amb' ? 'ambiant' : 'froid';
      const { error } = await supabase.from('elixir_prep_caisses').insert({ session_id: session.id, code, type: caisseType, scanned_by: user.id });
      if (error) { setScanMsg({ ok: false, txt: '❌ ' + error.message }); setBusy(false); return; }

      // Bug 1: await stock + mouvement (pas .catch silencieux)
      const { error: stockErr } = await supabase.from('elixir_prep_caisse_stock').upsert({
        code, status: 'en_livraison', current_partner_id: session.partner_id,
        current_partner_name: session.partner_name, date_sortie: new Date().toISOString(),
      }, { onConflict: 'code' });
      if (stockErr) { setScanMsg({ ok: false, txt: '⚠️ ' + code + ' scanné mais stock KO: ' + stockErr.message }); }

      const { error: mouvErr } = await supabase.from('elixir_prep_caisse_mouvements').insert({
        caisse_code: code, partner_id: session.partner_id, partner_name: session.partner_name,
        tournee: session.tournee || '', type: caisseType,
        date_depart: new Date().toISOString(), scanned_by_depart: user.id, session_id: session.id,
      });
      if (mouvErr) console.warn('Mouvement insert error:', mouvErr.message);

      // Incrémenter compteur
      const newCounts = { ...counts, [type]: counts[type] + 1 };
      await syncCounters(newCounts);

      // Bug 7: ajouter à la liste visible
      setScannedCaisses(prev => [{ code, type: caisseType }, ...prev]);
      setScanMsg({ ok: true, txt: '✅ ' + code + ' ' + (type === 'amb' ? 'amb' : 'froid') });
      setScanCode('');
      setTimeout(() => scanInputRef.current?.focus(), 100);
    } catch (e) { setScanMsg({ ok: false, txt: '❌ ' + e.message }); }
    finally { setBusy(false); setTimeout(() => setScanMsg(null), 2500); }
  }

  // ═══ PHOTO — via CameraCapture ═══
  async function handleCapturedPhoto(file) {
    setUploading(true);
    try {
      const ext = file.name?.split('.').pop() || 'jpg';
      const fileName = session.id + '/' + Date.now() + '_' + Math.random().toString(36).slice(2, 6) + '.' + ext;
      const toSend = await compressImage(file);
      const { error: uploadErr } = await supabase.storage.from('elixir-prep-photos').upload(fileName, toSend, { contentType: toSend.type || file.type || 'image/jpeg' });
      if (uploadErr) throw uploadErr;
      await supabase.from('elixir_prep_photos').insert({ session_id: session.id, url: fileName, storage_path: fileName, uploaded_by: user.id });
      setPhotoCount(prev => prev + 1);
    } catch (e) { console.error('Photo upload error:', e); throw e; }
    finally { setUploading(false); }
  }

  // ═══ PRÊTE ═══
  async function markReady() {
    const totalColis = counts.amb + counts.froid + counts.cartons;
    if (totalColis === 0) {
      alert('⚠️ Ajoute au moins 1 caisse ou carton avant de valider "Prête".');
      return;
    }
    // Une caisse doit porter un numéro (scanné ou tapé) ; un carton n'en a pas.
    // On recompte en base : la liste à l'écran peut être incomplète.
    const { count: nbIdentifiees } = await supabase.from('elixir_prep_caisses')
      .select('id', { count: 'exact', head: true }).eq('session_id', session.id);
    const sansNumero = (counts.amb + counts.froid) - (nbIdentifiees || 0);
    if (sansNumero > 0) {
      alert('⛔ ' + sansNumero + ' caisse(s) sans numéro.\n\nScanne la caisse, ou tape son numéro à la main si le scan ne marche pas.\n\n(Un carton seul ne bloque pas : il n\'a pas de numéro.)');
      return;
    }
    if (photoCount === 0 && !confirmPrete) { setConfirmPrete(true); return; }
    setConfirmPrete(false);

    // Sauvegarder les compteurs d'abord
    const update = {
      nb_amb: counts.amb, nb_froid: counts.froid, nb_cartons: counts.cartons, nb_cartons_vrac: 0,
      status: 'prete', updated_at: new Date().toISOString(),
    };
    await supabase.from('elixir_prep_sessions').update(update).eq('id', session.id);

    // Si cette session a un merge_target_id → fusionner avec la session dans Départs
    if (session.merge_target_id) {
      try {
        const merged = await mergeSession(session.id, session.merge_target_id);
        onSessionUpdate({ ...merged, _merged: true, _deletedId: session.id });
      } catch (e) {
        console.warn('Merge error:', e.message);
        onSessionUpdate({ ...session, ...update });
      }
    } else {
      onSessionUpdate({ ...session, ...update });
    }
    onClose();

    // Asana en arrière-plan (fire-and-forget) — charger les données nécessaires
    (async () => {
      try {
        const [caissesRes, anomRes, remarksRes, photosRes] = await Promise.all([
          supabase.from('elixir_prep_caisses').select('code, type').eq('session_id', session.id),
          supabase.from('elixir_prep_anomalies').select('*').eq('session_id', session.id),
          supabase.from('elixir_prep_remarks').select('*').eq('session_id', session.id),
          supabase.from('elixir_prep_photos').select('url, storage_path').eq('session_id', session.id),
        ]);
        const { syncSessionToAsana } = await import('../lib/asana.js');
        const taskGid = await syncSessionToAsana(
          session, user,
          caissesRes.data || [], anomRes.data || [], remarksRes.data || [],
          (photosRes.data || []).map(p => ({ storage_path: p.storage_path || p.url, url: p.url })),
          counts.cartons, 0
        );
        if (taskGid) {
          await supabase.from('elixir_prep_sessions').update({ asana_task_gid: taskGid }).eq('id', session.id);
        }
      } catch (e) { console.warn('QuickPanel Asana sync error:', e); }
    })();
  }

  // ═══ LIBÉRER ═══
  async function release() {
    const totalColis = (session.nb_amb || 0) + (session.nb_froid || 0) + (session.nb_cartons || 0) + (session.nb_cartons_vrac || 0);
    const hasWork = totalColis > 0 || photoCount > 0;

    if (hasWork) {
      const choice = confirm(
        'Libérer ' + session.partner_name + ' ?\n\n'
        + (totalColis > 0 ? '📦 ' + totalColis + ' colis\n' : '')
        + (photoCount > 0 ? '📸 ' + photoCount + ' photo(s)\n' : '')
        + '\nAppuie OK pour GARDER les colis/photos (un autre reprendra)\n'
        + 'Appuie Annuler pour tout effacer'
      );
      if (choice === undefined) return; // esc

      if (!choice) {
        // Annuler = tout supprimer (prépa seulement, pas stock caisses)
        if (!confirm('⚠️ Supprimer tous les colis et photos de ' + session.partner_name + ' ?')) return;
        await Promise.all([
          supabase.from('elixir_prep_photos').delete().eq('session_id', session.id),
          supabase.from('elixir_prep_caisses').delete().eq('session_id', session.id),
          supabase.from('elixir_prep_colis').delete().eq('session_id', session.id),
          supabase.from('elixir_prep_anomalies').delete().eq('session_id', session.id),
          supabase.from('elixir_prep_remarks').delete().eq('session_id', session.id),
        ]);
        // NOTE: on ne touche PAS caisse_stock ni mouvements
        const reset = {
          prepared_by: null, status: 'en_cours',
          nb_amb: 0, nb_froid: 0, nb_cartons: 0, nb_cartons_vrac: 0,
          updated_at: new Date().toISOString(),
        };
        await supabase.from('elixir_prep_sessions').update(reset).eq('id', session.id);
        onSessionUpdate({ ...session, ...reset });
        onClose();
        return;
      }
    } else {
      if (!confirm('Libérer ' + session.partner_name + ' ?')) return;
    }

    // OK = garder les données
    const update = {
      prepared_by: null, status: 'en_cours',
      updated_at: new Date().toISOString(),
    };
    await supabase.from('elixir_prep_sessions').update(update).eq('id', session.id);
    onSessionUpdate({ ...session, ...update });
    onClose();
  }

  const tLabel = session.tournee === 'IMPERATIFS' ? 'Imp.' : session.tournee === 'CHRONOPOST' ? 'Chron.' : session.tournee ? 'T' + session.tournee : '';

  return (
    <>
      {!cameraOpen && <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 9990 }} />}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9991,
        background: 'white', borderTopLeftRadius: 20, borderTopRightRadius: 20,
        boxShadow: '0 -4px 24px rgba(0,0,0,0.18)', maxHeight: '85vh',
        display: cameraOpen ? 'none' : 'flex', flexDirection: 'column',
        animation: 'slideUp 0.25s ease-out',
      }}>
        <style>{`@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>

        {/* Scrollable content */}
        <div style={{ overflowY: 'auto', flex: 1, paddingBottom: 80 }}>

        <div style={{ textAlign: 'center', padding: '6px 0 0' }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: '#DDD', margin: '0 auto' }} />
        </div>

        {/* Header */}
        <div style={{ padding: '4px 16px 6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#1A1D23', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{session.partner_name}</div>
            <div style={{ fontSize: 10, color: '#888' }}>{session.adresse || ''}{tLabel ? ' · ' + tLabel : ''}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#1A5276' }}>{productCount}</div>
              <div style={{ fontSize: 7, color: '#888', fontWeight: 600 }}>produits</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#D4680A' }}>{totalQty}</div>
              <div style={{ fontSize: 7, color: '#888', fontWeight: 600 }}>boîtes</div>
            </div>
            {anomalyCount > 0 && (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#C0392B' }}>{anomalyCount}</div>
                <div style={{ fontSize: 7, color: '#C0392B', fontWeight: 600 }}>anomalie{anomalyCount > 1 ? 's' : ''}</div>
              </div>
            )}
          </div>
        </div>

        {/* Info fusion — cette prépa sera ajoutée aux départs existants */}
        {session.merge_target_id && (
          <div style={{ padding: '0 16px 6px' }}>
            <div style={{ padding: '6px 10px', borderRadius: 6, background: '#EEF6FF', border: '1px solid #B3D4F0', fontSize: 10, color: '#1A5276', fontWeight: 600 }}>
              📦 Déjà dans les départs — sera fusionnée quand tu cliques "Prête"
            </div>
          </div>
        )}

        {/* PHOTO */}
        <div style={{ padding: '0 16px 8px' }}>
          <button onClick={() => setCameraOpen(true)} disabled={uploading} style={{
            width: '100%', padding: '10px 12px', borderRadius: 10, border: '2px dashed #1A5276',
            background: '#EEF6FF', display: 'flex', alignItems: 'center', gap: 8,
            cursor: 'pointer', fontFamily: 'inherit', opacity: uploading ? 0.6 : 1,
          }}>
            <span style={{ fontSize: 20 }}>📸</span>
            <div style={{ flex: 1, textAlign: 'left' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1A5276' }}>{uploading ? 'Envoi…' : 'Photo'}</div>
            </div>
            <div style={{
              background: photoCount > 0 ? '#16A34A' : '#1A5276', color: 'white',
              width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 800,
            }}>{photoCount}</div>
          </button>
        </div>

        {/* CAISSES + CARTONS */}
        <div style={{ padding: '0 16px 6px' }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: '#888', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Caisses</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5, marginBottom: 6 }}>
            {['amb', 'froid'].map(type => (
              <Counter key={type} label={TYPE_LABELS[type]} value={counts[type]}
                color={TYPE_COLORS[type]} bg={TYPE_BG[type]} border={TYPE_COLORS[type]}
                onMinus={() => removeColis(type)} onPlus={() => addColis(type)} disabled={busy} />
            ))}
          </div>

          {/* SCAN CAISSE */}
          <div style={{ marginBottom: 6 }}>
            <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
              <input
                ref={scanInputRef}
                type="text" inputMode="text"
                value={scanCode}
                onChange={e => setScanCode(e.target.value.toUpperCase())}
                onKeyDown={e => { if (e.key === 'Enter' && scanCode.trim()) e.preventDefault(); }}
                placeholder="Scanner caisse (C1, C42…)"
                style={{ flex: 1, padding: '7px 10px', borderRadius: 8, border: '1.5px solid #D6E4F0', fontSize: 12, fontFamily: 'inherit' }}
              />
            </div>
            {scanCode.trim() && (
              <div style={{ display: 'flex', gap: 4 }}>
                <button onClick={() => scanCaisse('amb')} disabled={busy}
                  style={{ flex: 1, padding: '8px', borderRadius: 8, border: '1.5px solid #D4680A', background: '#FFF8F0', cursor: 'pointer', fontFamily: 'inherit', fontSize: 11, fontWeight: 700, color: '#D4680A' }}>
                  📦 Amb
                </button>
                <button onClick={() => scanCaisse('froid')} disabled={busy}
                  style={{ flex: 1, padding: '8px', borderRadius: 8, border: '1.5px solid #1A5276', background: '#F0F6FF', cursor: 'pointer', fontFamily: 'inherit', fontSize: 11, fontWeight: 700, color: '#1A5276' }}>
                  ❄️ Froid
                </button>
              </div>
            )}
            {scanMsg && (
              <div style={{ marginTop: 3, fontSize: 11, fontWeight: 600, color: scanMsg.ok ? '#1E6B3C' : '#C0392B', textAlign: 'center' }}>
                {scanMsg.txt}
              </div>
            )}
          </div>

          {/* Bug 7: Liste caisses scannées cette session */}
          {scannedCaisses.length > 0 && (
            <div style={{ marginBottom: 6, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              {scannedCaisses.map((c, i) => (
                <span key={i} style={{
                  padding: '2px 6px', borderRadius: 5, fontSize: 10, fontWeight: 700, fontFamily: 'monospace',
                  background: c.type === 'ambiant' ? '#FFF8F0' : '#F0F6FF',
                  color: c.type === 'ambiant' ? '#D4680A' : '#1A5276',
                  border: '1px solid ' + (c.type === 'ambiant' ? '#F5CBA7' : '#B3D4F0'),
                }}>{c.code}</span>
              ))}
            </div>
          )}

          <div style={{ fontSize: 9, fontWeight: 700, color: '#888', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Cartons</div>
          <Counter label="CARTONS" value={counts.cartons}
            color="#444" bg="#F8F8FA" border="#444"
            onMinus={() => removeColis('cartons')} onPlus={() => addColis('cartons')} disabled={busy} />
        </div>

        </div>{/* Fin scrollable */}

        {/* Boutons FIXÉS en bas — toujours visibles */}
        <div style={{ borderTop: '1px solid #E8E8EE', background: 'white', flexShrink: 0 }}>
        {confirmPrete && (
          <div style={{ padding: '6px 16px 0' }}>
            <div style={{ padding: '6px 10px', borderRadius: 6, background: '#FEF3CD', border: '1px solid #F5CBA7', fontSize: 11, color: '#7D5A0A', textAlign: 'center' }}>
              Pas de photo — continuer ?
            </div>
          </div>
        )}

        <div style={{ padding: '8px 16px 20px', display: 'flex', gap: 8 }}>
          <button onClick={markReady} style={{
            flex: 1, padding: 12, borderRadius: 10, border: 'none',
            background: confirmPrete ? '#D4680A' : '#16A34A', color: 'white',
            fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
          }}>{confirmPrete ? 'Oui, Prête' : 'Prête'}</button>
          <button onClick={() => { onClose(); onOpenDetail(session); }} style={{
            padding: '12px 14px', borderRadius: 10, border: '1.5px solid #E8E8EE',
            background: 'white', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', color: '#888',
          }}>Fiche</button>
          <button onClick={() => { onClose(); onOpenDetail(session, 'anomalies'); }} style={{
            padding: '12px 14px', borderRadius: 10, border: '1.5px solid #FDECEA',
            background: '#FFF5F5', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', color: '#C0392B',
          }}>Anomalie</button>
          <button onClick={release} style={{
            padding: '12px 10px', borderRadius: 10, border: '1.5px solid #E8E8EE',
            background: 'white', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', color: '#888',
          }}>Libérer</button>
        </div>
        </div>
      </div>
      <CameraCapture open={cameraOpen} onClose={() => setCameraOpen(false)} onCapture={handleCapturedPhoto} />
    </>
  );
}

function Counter({ label, value, color, bg, border, dashed, onMinus, onPlus, disabled }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '6px 8px', borderRadius: 8,
      border: '1.5px ' + (dashed ? 'dashed' : 'solid') + ' ' + border, background: bg,
    }}>
      <button onClick={onMinus} disabled={disabled || value === 0} style={{
        width: 28, height: 28, borderRadius: '50%', border: '1px solid #DDD',
        background: 'white', fontSize: 16, cursor: value > 0 ? 'pointer' : 'default',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'inherit', color: '#444', opacity: value > 0 ? 1 : 0.3,
      }}>−</button>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 18, fontWeight: 800, color }}>{value}</div>
        <div style={{ fontSize: 7, fontWeight: 700, color, letterSpacing: 0.3 }}>{label}</div>
      </div>
      <button onClick={onPlus} disabled={disabled} style={{
        width: 28, height: 28, borderRadius: '50%', border: '1px solid #DDD',
        background: 'white', fontSize: 16, cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'inherit', color: '#444',
      }}>+</button>
    </div>
  );
}
